import type { ComponentPropsWithRef, ReactNode } from "react";
import { cn, DISABLED, FOCUS_RING } from "./cn";
import { Spinner } from "./Spinner";

export const BUTTON_VARIANTS = {
  primary: "bg-primary-500 text-on-primary hover:bg-primary-400 active:bg-primary-700 shadow-sm",
  secondary: "bg-surface text-content border border-line hover:bg-subtle active:bg-subtle",
  outline: "border border-line text-content hover:bg-subtle active:bg-subtle",
  ghost: "text-content-secondary hover:bg-subtle hover:text-content active:bg-subtle",
  soft: "bg-subtle text-content-secondary border border-line hover:bg-line hover:text-content",
  dashed:
    "border border-dashed border-primary-500/50 text-primary-500 hover:bg-primary-500/10 active:bg-primary-500/20",
  danger: "bg-error text-white hover:brightness-95 active:brightness-90 shadow-sm",
  dangerSoft: "bg-error/10 text-error hover:bg-error/20 active:bg-error/25",
  warningSoft: "bg-warning/10 text-warning hover:bg-warning/20 active:bg-warning/25",
  successSoft: "bg-success/10 text-success hover:bg-success/20 active:bg-success/25",
  infoSoft: "bg-info/10 text-info hover:bg-info/20 active:bg-info/25",
  info: "bg-info text-white hover:bg-info/90 active:bg-info/80 shadow-sm",
  link: "text-primary-500 hover:underline underline-offset-2 p-0 h-auto",
} as const satisfies Record<string, string>;

export type ButtonVariant = keyof typeof BUTTON_VARIANTS;

export const BUTTON_SIZES = ["xs", "sm", "md", "lg"] as const;

export type ButtonSize = (typeof BUTTON_SIZES)[number];

export const BUTTON_SIZE_CLASSES = {
  xs: "[@media(pointer:coarse)]:min-h-11 h-7 px-2 text-xs gap-1",
  sm: "[@media(pointer:coarse)]:min-h-11 h-8 px-3 text-sm gap-1.5",
  md: "[@media(pointer:coarse)]:min-h-11 h-10 px-4 text-sm gap-2",
  lg: "[@media(pointer:coarse)]:min-h-11 h-12 px-6 text-base gap-2",
} as const satisfies Record<ButtonSize, string>;

export const BUTTON_ICON_SIZE_CLASSES = {
  xs: "[@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 h-7 w-7 p-0",
  sm: "[@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 h-8 w-8 p-0",
  md: "[@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 h-10 w-10 p-0",
  lg: "[@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 h-12 w-12 p-0",
} as const satisfies Record<ButtonSize, string>;

const SPINNER_FOR_SIZE = {
  xs: "sm",
  sm: "sm",
  md: "sm",
  lg: "md",
} as const satisfies Record<ButtonSize, "sm" | "md">;

export const BUTTON_SHAPES = {
  default: "rounded-lg",
  pill: "rounded-full",
} as const satisfies Record<string, string>;

export type ButtonShape = keyof typeof BUTTON_SHAPES;

export interface ButtonProps extends ComponentPropsWithRef<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  icon?: ReactNode;
  iconRight?: ReactNode;
  loading?: boolean;
  iconOnly?: boolean;
  fullWidth?: boolean;
}

export function Button({
  variant = "primary",
  size = "md",
  shape = "default",
  icon,
  iconRight,
  loading = false,
  iconOnly = false,
  fullWidth = false,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  const geometry =
    variant === "link"
      ? undefined
      : iconOnly
        ? BUTTON_ICON_SIZE_CLASSES[size]
        : BUTTON_SIZE_CLASSES[size];

  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors",
        BUTTON_SHAPES[shape],
        FOCUS_RING,
        DISABLED,
        BUTTON_VARIANTS[variant],
        geometry,
        fullWidth ? "w-full" : undefined,
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={SPINNER_FOR_SIZE[size]} /> : icon}
      {iconOnly ? null : children}
      {iconOnly || loading ? null : iconRight}
    </button>
  );
}
